/**
 * Workspace = the unit of data ownership.
 *
 * In local mode (v0.7), there's exactly one workspace named DEFAULT_WORKSPACE_ID
 * and the user never sees this concept — UI talks to a single workspace
 * implicitly. The plumbing exists so multi-user / household sharing in the SaaS
 * phase becomes a feature flag flip instead of a schema migration.
 *
 * Entities (Debt, IncomeSource, MonthlyLedgerEntry, RecurringExpense...) carry
 * an optional `workspaceId` field. When unset, repos treat the entity as part
 * of DEFAULT_WORKSPACE_ID. Once SaaS lands, the field becomes required and
 * `getCurrentWorkspaceId()` reads from the auth/session context.
 */

export const DEFAULT_WORKSPACE_ID = 'default-local'

export type WorkspaceRole = 'owner' | 'editor' | 'viewer'

export interface WorkspaceMember {
  userId: string
  role: WorkspaceRole
  invitedAt: string
  joinedAt?: string
}

export interface Workspace {
  id: string
  name: string
  /** Single owner today; multi-member roster lands with SaaS auth */
  members: WorkspaceMember[]
  createdAt: string
  updatedAt: string
}

export function defaultWorkspace(): Workspace {
  const now = new Date().toISOString()
  return {
    id: DEFAULT_WORKSPACE_ID,
    name: 'Personal',
    members: [],
    createdAt: now,
    updatedAt: now,
  }
}

/**
 * Returns the workspaceId an entity belongs to. Falls back to the default for
 * pre-workspace entities (so existing localStorage data keeps working).
 */
export function workspaceIdOf(entity: { workspaceId?: string }): string {
  return entity.workspaceId ?? DEFAULT_WORKSPACE_ID
}

/**
 * Pluggable resolver for the active workspace id. Default is the local
 * fallback; `AuthProvider` swaps in a function that reads from the auth
 * store once the auth module has loaded.
 *
 * Using a function-pointer setter rather than a direct import keeps this
 * module dependency-free of the auth/store layer — important because
 * `getCurrentWorkspaceId()` is called from very early code paths during
 * module init (stores reference it on first read).
 */
let workspaceIdResolver: () => string = () => DEFAULT_WORKSPACE_ID

export function setWorkspaceIdResolver(fn: () => string): void {
  workspaceIdResolver = fn
}

/**
 * Returns the active workspace id for the signed-in user, or the local
 * fallback (`DEFAULT_WORKSPACE_ID`) when no session is active.
 *
 * In v0.7 (local-first only) this always returned `DEFAULT_WORKSPACE_ID`.
 * In v0.8 (SaaS) `AuthProvider` registers a resolver that reads from the
 * auth store, so this returns the user's workspace once they sign in.
 * The `workspaceIdOf()` fallback above continues to handle pre-workspace
 * legacy local entities the same way it always has.
 */
export function getCurrentWorkspaceId(): string {
  return workspaceIdResolver()
}
